import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FooterLogo } from "./footer/FooterLogo";
import { FooterSection } from "./footer/FooterSection";
import { FooterCopyright } from "./footer/FooterCopyright";

export type FooterContent = {
  id: string;
  section_name: string;
  title: string;
  content: string | null;
  link_url: string | null;
  order_position: number;
}

type GroupedContent = {
  [key: string]: FooterContent[];
}

export function Footer() {
  const [footerContent, setFooterContent] = useState<GroupedContent>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFooterContent = async () => {
      try {
        const { data, error } = await supabase
          .from('footer_content')
          .select('*')
          .order('order_position');

        if (error) {
          console.error('Error fetching footer content:', error);
          setError(error.message);
          return;
        }

        // Group content by section_name
        const grouped = (data || []).reduce((acc: GroupedContent, item: FooterContent) => {
          if (!acc[item.section_name]) {
            acc[item.section_name] = [];
          }
          acc[item.section_name].push(item);
          return acc;
        }, {});

        setFooterContent(grouped);
      } catch (err) {
        console.error('Error in fetchFooterContent:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchFooterContent();

    // Subscribe to real-time changes
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'footer_content'
        },
        () => {
          fetchFooterContent();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const renderContent = (item: FooterContent) => {
    if (item.link_url) {
      return item.link_url.startsWith('mailto:') ? (
        <a href={item.link_url} className="text-gray-600 hover:text-primary-600 transition-colors">
          {item.content || item.title}
        </a>
      ) : (
        <Link to={item.link_url} className="text-gray-600 hover:text-primary-600 transition-colors">
          {item.title}
        </Link>
      );
    }
    return <p className="text-gray-600">{item.content || item.title}</p>;
  };

  if (error) {
    console.error('Footer error:', error);
  }

  if (loading) {
    return (
      <footer className="bg-gray-100 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gray-100 border-t">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col items-center mb-8">
          <FooterLogo />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {Object.entries(footerContent).map(([section, items]) => (
            <FooterSection
              key={section}
              title={section}
              items={items}
              renderContent={renderContent}
            />
          ))}
        </div>

        <FooterCopyright />
      </div>
    </footer>
  );
}